// #region imports

// --- Angular
import { Injectable } from '@angular/core';

// --- RxJs
import { Observable, BehaviorSubject, Subject } from 'rxjs';

// -- HS Code
import {
  EmailAddressValidationModel,
  SettingsModel,
  UserDetailsModel,
  UserProfileModel,
} from '../../../models';

// #endregion

@Injectable({
  providedIn: 'root',
})
export abstract class ProfileBaseService {
  protected _isLoaded: boolean = false;

  protected _settings$: BehaviorSubject<SettingsModel | null>;
  get settings$(): Observable<SettingsModel | null> {
    return this._settings$;
  }

  protected _userDetails$: BehaviorSubject<UserDetailsModel | null>;
  get userDetails$(): Observable<UserDetailsModel | null> {
    return this._userDetails$;
  }

  protected _updateUserDetailsError$: BehaviorSubject<string>;
  get updateUserDetailsError$(): Observable<string> {
    return this._updateUserDetailsError$;
  }

  protected _updateUserDetailsResult$: Subject<boolean>;
  get updateUserDetailsResult$(): Observable<boolean> {
    return this._updateUserDetailsResult$;
  }

  protected _updateLoginDetailsError$: BehaviorSubject<string>;
  get updateLoginDetailsError$(): Observable<string> {
    return this._updateLoginDetailsError$;
  }

  protected _updateLoginDetailsResult$: Subject<boolean>;
  get updateLoginDetailsResult$(): Observable<boolean> {
    return this._updateLoginDetailsResult$;
  }

  protected _isEmailAddressInUse$: BehaviorSubject<EmailAddressValidationModel | null>;
  get isEmailAddressInUse$(): Observable<EmailAddressValidationModel | null> {
    return this._isEmailAddressInUse$;
  }

  protected _isValidatingEmailAddress$: BehaviorSubject<boolean>;
  get isValidatingEmailAddress$(): Observable<boolean> {
    return this._isValidatingEmailAddress$;
  }

  protected _isUpdating$: BehaviorSubject<boolean>;
  get isUpdating$(): Observable<boolean> {
    return this._isUpdating$;
  }

  protected _updatingAvatar$: BehaviorSubject<boolean>;
  get updatingAvatar$(): Observable<boolean> {
    return this._updatingAvatar$;
  }

  protected _avatarUpdated$: Subject<boolean>;
  get avatarUpdated$(): Observable<boolean> {
    return this._avatarUpdated$;
  }

  protected _removingAvatar$: BehaviorSubject<boolean>;
  get removingAvatar$(): Observable<boolean> {
    return this._removingAvatar$;
  }

  protected _avatarRemoved$: Subject<boolean>;
  get avatarRemoved$(): Observable<boolean> {
    return this._avatarRemoved$;
  }

  constructor() {
    this._settings$ = new BehaviorSubject<SettingsModel | null>(null);
    this._userDetails$ = new BehaviorSubject<UserDetailsModel | null>(null);
    this._updateUserDetailsError$ = new BehaviorSubject<string>('');
    this._updateUserDetailsResult$ = new Subject<boolean>();
    this._updateLoginDetailsError$ = new BehaviorSubject<string>('');
    this._updateLoginDetailsResult$ = new Subject<boolean>();
    this._isEmailAddressInUse$ =
      new BehaviorSubject<EmailAddressValidationModel | null>(null);
    this._isValidatingEmailAddress$ = new BehaviorSubject<boolean>(false);
    this._isUpdating$ = new BehaviorSubject<boolean>(false);
    this._updatingAvatar$ = new BehaviorSubject<boolean>(false);
    this._avatarUpdated$ = new Subject<boolean>();
    this._removingAvatar$ = new BehaviorSubject<boolean>(false);
    this._avatarRemoved$ = new Subject<boolean>();
  }

  abstract loadProfile(): void;
  abstract update(userDetails: UserProfileModel): Observable<boolean>;
  abstract updateUserDetails(userDetails: UserDetailsModel): void;
  abstract updateLoginDetails(loginDetails: UserDetailsModel): void;
  abstract checkIfEmailAddressInUse(emailAddress: string): void;
  abstract updateAvatar(croppedImage: string): void;
  abstract removeAvatar(): void;
  abstract requestPasswordChange(): void;
  abstract refreshEmailAddress(email: string): void;
}
